import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

import Button from '../common/Button';
import { useAuthStore } from '../../store/authStore';
import { useReadingsStore } from '../../store/readingsStore';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading, error, clearError, resetPassword } = useAuthStore();
  const { restorePendingReading } = useReadingsStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      clearError();
      setResetError(null);
    };
  }, [clearError, isResetting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetSent(false);
    setResetError(null);

    try {
      await signIn(email, password);

      const { user, error: authError } = useAuthStore.getState();
      if (!authError && user) {
        if (restorePendingReading()) {
          navigate('/interpretation');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  const handlePasswordReset = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      setResetError('Please enter your email address');
      return;
    }

    try {
      setResetError(null);
      await resetPassword(email);
      setResetSent(true);
    } catch (error) {
      setResetError(error instanceof Error ? error.message : 'Failed to send reset email');
    }
  };

  const toggleMode = () => {
    setIsResetting(!isResetting);
    setResetSent(false);
    setResetError(null);
    clearError();
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md mx-auto py-16 px-4"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
            Welcome Back
          </h1>
          <p className="mt-3 text-ink/60 dark:text-parchment/50 font-sans text-sm">
            {isResetting
              ? 'Enter your email to receive password reset instructions'
              : 'Log in to access your saved readings and continue your journey.'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && !isResetting && (
            <div className="bg-sun/10 dark:bg-ember/10 text-sun dark:text-ember p-3 rounded-lg flex items-center gap-2 text-sm">
              <span>{error}</span>
            </div>
          )}

          {resetError && isResetting && (
            <div className="bg-sun/10 dark:bg-ember/10 text-sun dark:text-ember p-3 rounded-lg flex items-center gap-2 text-sm">
              <span>{resetError}</span>
            </div>
          )}

          {resetSent && isResetting && (
            <div className="bg-moon/10 dark:bg-moon/20 text-moon dark:text-parchment/80 p-3 rounded-lg text-sm">
              Password reset instructions have been sent to your email.
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 focus:border-sun dark:focus:border-ember bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist transition-colors duration-200"
              placeholder="Enter your email"
              autoComplete="email"
            />
          </div>

          {!isResetting && (
            <div>
              <label htmlFor="password" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 focus:border-sun dark:focus:border-ember bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist transition-colors duration-200"
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={toggleMode}
              className="text-sm font-display text-sun dark:text-ember hover:text-ember dark:hover:text-sun transition-colors"
            >
              {isResetting ? 'Back to Login' : 'Forgot Password?'}
            </button>
          </div>

          <Button
            type={isResetting ? 'button' : 'submit'}
            variant="primary"
            fullWidth
            isLoading={loading}
            onClick={isResetting ? handlePasswordReset : undefined}
          >
            {isResetting ? 'Send Reset Instructions' : 'Log In'}
          </Button>

          <p className="text-center text-ink/60 dark:text-parchment/50 font-sans text-sm">
            Don't have an account?{' '}
            <Link to="/signup" className="font-display text-sun dark:text-ember hover:text-ember dark:hover:text-sun transition-colors">
              Sign Up
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginPage;
